import React, { ReactElement } from 'react';
import { Modal } from 'react-bootstrap';
import i18next from 'i18next';
import { ErrorMessage, Field, Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import { Typeahead } from 'react-bootstrap-typeahead';
import { AvailableFeatureEnum } from '../../../../../shared/utils/enums';
import { Option } from 'react-bootstrap-typeahead/types/types';
import { AppManagementInputDto } from '../../../../../shared/dtos/appManagement.dto';

type AppManagementForm = {
  appVersion: string,
  featureList: AvailableFeatureEnum[],
  deText: string,
  enText: string,
  textColor: string,
  backgroundColor: string,
  isButton: boolean,
  borderColor: string,
}

type Props = {
  onCreate: () => void;
};

const AppManagementModal = ({ onCreate }: Props): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const createAppManagement = async (values: AppManagementForm): Promise<void> => {
    const body: AppManagementInputDto = {
      latestVersion: null,
      updateReqVersion: values.appVersion,
      featureList: values.featureList,
      loginBottomPanel: JSON.stringify({
        deText: values.deText,
        enText: values.enText,
        isButton: values.isButton,
        textColor: values.textColor,
        backgroundColor: values.backgroundColor,
        borderColor: values.isButton ? values.borderColor : undefined,
      }),
    };

    await authFetch('/api/v1/admin/version/update/', { method: 'POST', body });
    onCreate();
  };


  const AppManagementForm = (): ReactElement => {
    return (
      <Formik<AppManagementForm>
        initialValues={{
          appVersion: '',
          featureList: [],
          deText: '',
          enText: '',
          textColor: '#000000',
          backgroundColor: '#ffffff',
          isButton: false,
          borderColor: '',
        }}
        validationSchema={Yup.object({
          appVersion: Yup.string()
            .required(i18next.t('generic.required'))
            .matches(/^\d+\.\d+\.\d+$/, i18next.t('appManagementModal.validation.appVersion')),
          deText: Yup.string().required(i18next.t('generic.required')),
          enText: Yup.string().required(i18next.t('generic.required')),
          textColor: Yup.string().required(i18next.t('generic.required')),
          backgroundColor: Yup.string().required(i18next.t('generic.required')),
          borderColor: Yup.string().required(i18next.t('generic.required')),
        })}
        onSubmit={(values: AppManagementForm) => createAppManagement(values)}
      >
        {({ isSubmitting, setFieldValue, values, setTouched, handleSubmit }: FormikProps<AppManagementForm>) => (
          <Form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label>{i18next.t('appManagementModal.appVersion')}</label>
              <Field name="appVersion" className="form-control" />
              <ErrorMessage name="appVersion" component="div" className="text-danger" />
            </div>

            <div className="mb-3">
              <label>{i18next.t('appManagementModal.featureList')}</label>
              <Typeahead
                id="featureList"
                multiple
                onChange={(selected: Option[]) => setFieldValue('featureList', selected)}
                onBlur={() => setTouched({ featureList: true })}
                options={Object.values(AvailableFeatureEnum)}
                selected={values.featureList}
                placeholder="Features auswählen..."
              />
              <ErrorMessage name="featureList" component="div" className="text-danger" />
            </div>

            <hr />
            <h5>{i18next.t('appManagementModal.bottomPanelSection')}</h5>

            <div className="mb-3">
              <label>{i18next.t('appManagementModal.deText')}</label>
              <Field name="deText" className="form-control" />
              <ErrorMessage name="deText" component="div" className="text-danger" />
            </div>

            <div className="mb-3">
              <label>{i18next.t('appManagementModal.enText')}</label>
              <Field name="enText" className="form-control" />
              <ErrorMessage name="enText" component="div" className="text-danger" />
            </div>

            <div className="mb-3">
              <label>{i18next.t('appManagementModal.textColor')}</label>
              <Field name="textColor" type="color" className="form-control form-control-color" />
              <ErrorMessage name="textColor" component="div" className="text-danger" />
            </div>

            <div className="mb-3">
              <label>{i18next.t('appManagementModal.backgroundColor')}</label>
              <Field name="backgroundColor" type="color" className="form-control form-control-color" />
              <ErrorMessage name="backgroundColor" component="div" className="text-danger" />
            </div>

            <div className="mb-3 form-check">
              <Field
                name="isButton"
                type="checkbox"
                className="form-check-input"
                id="isButtonCheck"
                checked={values.isButton}
                onChange={() => setFieldValue('isButton', !values.isButton)}
              />
              <label className="form-check-label" htmlFor="isButtonCheck">{i18next.t('appManagementModal.isButton')}</label>
            </div>

            {values.isButton && (
              <div className="mb-3">
                <label>{i18next.t('appManagementModal.buttonBorderColor')}</label>
                <Field name="buttonBorderColor" type="color" className="form-control form-control-color" />
                <ErrorMessage name="buttonBorderColor" component="div" className="text-danger" />
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ?
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> :
                i18next.t('generic.save')
              }
            </button>
          </Form>
        )}
      </Formik>
    );
  };


  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>
          {i18next.t('appManagementModal.title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AppManagementForm />
      </Modal.Body>
    </>
  );
};

export default AppManagementModal;
